import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@carbon/react";
import axios from "../../axios-orders";
import { Loader } from "../../components";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import { setCookie } from "../../utils";
import { useAuth } from "../../context/hooks";
import "./impersonation.scss";

const MIN_COOKIE_DURATION_DAYS = 1 / 24; // Minimum of 1 hour

const parseCookieDuration = (expiresAtParam: string | null) => {
  if (!expiresAtParam) {
    return MIN_COOKIE_DURATION_DAYS;
  }

  const expiresAt = Number(expiresAtParam);
  if (Number.isNaN(expiresAt)) {
    return MIN_COOKIE_DURATION_DAYS;
  }

  const diffMs = expiresAt - Date.now();
  if (diffMs <= 0) {
    return MIN_COOKIE_DURATION_DAYS;
  }

  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return Math.max(diffDays, MIN_COOKIE_DURATION_DAYS);
};

const ImpersonationLanding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authCheckState } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const bootstrap = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setError("Token de acceso no encontrado.");
        return;
      }

      const redirect = searchParams.get("redirect") || "/";
      const expiresAtParam = searchParams.get("expiresAt");
      const userIdParam = searchParams.get("userId");
      const impersonatorId = searchParams.get("impersonatorId");

      const cookieDurationDays = parseCookieDuration(expiresAtParam);

      setCookie("accessToken", token, cookieDurationDays);
      localStorage.setItem("accessToken", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      let userId = userIdParam;

      if (!userId) {
        try {
          const meResponse = await axios.get("/user/me");
          userId = meResponse?.data?.user?.id;
        } catch (fetchError) {
          console.error(
            "Failed to resolve user during impersonation",
            fetchError
          );
          throw new Error("No se pudo obtener el usuario asociado al token.");
        }
      }

      if (!userId) {
        throw new Error("Identificador de usuario no disponible.");
      }

      setCookie("userId", userId, cookieDurationDays);
      localStorage.setItem("userId", userId);

      const impersonationContext = {
        startedAt: new Date().toISOString(),
        expiresAt: expiresAtParam ? Number(expiresAtParam) : null,
        impersonatorId: impersonatorId || null,
      };

      sessionStorage.setItem(
        "impersonationContext",
        JSON.stringify(impersonationContext)
      );

      await authCheckState();

      if (isMounted) {
        navigate(redirect, { replace: true });
      }
    };

    bootstrap().catch((bootstrapError: Error) => {
      console.error("Impersonation bootstrap failed", bootstrapError);
      if (isMounted) {
        setError(
          bootstrapError.message ||
            "No se pudo completar el inicio de sesión con este enlace."
        );
      }
    });

    return () => {
      isMounted = false;
    };
  }, [searchParams, authCheckState, navigate]);

  if (error) {
    return (
      <AuthLayout>
        <div className="impersonation-error">
          <h1 className="impersonation-error__title">
            No se pudo iniciar sesión
          </h1>
          <p className="impersonation-error__copy">{error}</p>
          <Button onClick={() => navigate("/", { replace: true })}>
            Ir al inicio
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return <Loader isFullScreen />;
};

export default ImpersonationLanding;
